import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../config/api';
import {
  FiCpu, FiRefreshCw, FiChevronDown, FiChevronRight,
  FiCheckCircle, FiXCircle, FiAlertTriangle, FiDollarSign,
  FiMail, FiFileText, FiShoppingBag, FiHome, FiZap, FiFilter,
  FiThumbsUp, FiThumbsDown,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { tw, monoFont } from '../utils/terminalTheme';

// ─── Types ────────────────────────────────────────────────────────
interface IntelligenceRecord {
  id: number;
  source_type: string;
  source_id: number;
  classification: string | null;
  classification_confidence: number | null;
  vendor: string | null;
  amount: number | null;
  due_date: string | null;
  service_date: string | null;
  description: string | null;
  property_id: number | null;
  property_name: string | null;
  property_match_method: string | null;
  pocketsmith_transaction_id: number | null;
  ledger_match_confidence: number | null;
  ledger_match_method: string | null;
  summary: string | null;
  processed_at: string | null;
  created_at: string | null;
  email_subject: string | null;
  email_sender: string | null;
  model_used: string | null;
  feedback_correct: boolean | null;
  feedback_notes: string | null;
}

interface ModelStats {
  total: number;
  reviewed: number;
  correct: number;
  accuracy: number | null;
}

interface Stats {
  total: number;
  by_classification: Record<string, number>;
  by_source: Record<string, number>;
  matched: number;
  unmatched_bills: number;
  with_property: number;
  avg_confidence: number | null;
  reviewed: number;
  correct: number;
  accuracy: number | null;
  by_model: Record<string, ModelStats>;
}

interface ListResponse {
  total: number;
  offset: number;
  limit: number;
  items: IntelligenceRecord[];
}

// ─── Classification color map ─────────────────────────────────────
const classificationColors: Record<string, { bg: string; text: string; border: string }> = {
  bill:                   { bg: 'bg-red-500/15',     text: 'text-red-400',     border: 'border-red-500/30' },
  receipt:                { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  payment_confirmation:   { bg: 'bg-cyan-500/15',   text: 'text-cyan-400',    border: 'border-cyan-500/30' },
  notice:                 { bg: 'bg-blue-500/15',    text: 'text-blue-400',    border: 'border-blue-500/30' },
  marketing:              { bg: 'bg-purple-500/15',  text: 'text-purple-400',  border: 'border-purple-500/30' },
  noise:                  { bg: 'bg-gray-500/15',    text: 'text-gray-500',    border: 'border-gray-500/30' },
};

const sourceIcons: Record<string, React.ReactNode> = {
  document: <FiFileText className="w-3.5 h-3.5 text-amber-400" />,
  hoa_email: <FiHome className="w-3.5 h-3.5 text-purple-400" />,
  retailer_email: <FiShoppingBag className="w-3.5 h-3.5 text-blue-400" />,
};

const sourceLabels: Record<string, string> = {
  document: 'Doc',
  hoa_email: 'HOA',
  retailer_email: 'Retail',
};

// ─── Component ────────────────────────────────────────────────────
export const EmailIntelligence: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [records, setRecords] = useState<IntelligenceRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [reprocessingId, setReprocessingId] = useState<number | null>(null);
  const [backfilling, setBackfilling] = useState(false);

  // Filters
  const [filterClass, setFilterClass] = useState<string>('');
  const [filterSource, setFilterSource] = useState<string>('');
  const [filterMatch, setFilterMatch] = useState<string>('');
  const [offset, setOffset] = useState(0);
  const PAGE_SIZE = 50;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, any> = { limit: PAGE_SIZE, offset };
      if (filterClass) params.classification = filterClass;
      if (filterSource) params.source_type = filterSource;
      if (filterMatch === 'matched') params.has_ledger_match = true;
      if (filterMatch === 'unmatched') params.has_ledger_match = false;

      const [statsRes, listRes] = await Promise.all([
        api.get('/email-intelligence/stats'),
        api.get('/email-intelligence/', { params }),
      ]);

      setStats(statsRes.data);
      setRecords((listRes.data as ListResponse).items);
      setTotalRecords((listRes.data as ListResponse).total);
    } catch (error) {
      console.error('Failed to load intelligence data:', error);
      toast.error('Failed to load email intelligence data');
    } finally {
      setLoading(false);
    }
  }, [filterClass, filterSource, filterMatch, offset]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset pagination when filters change
  useEffect(() => {
    setOffset(0);
  }, [filterClass, filterSource, filterMatch]);

  const handleReprocess = async (sourceType: string, sourceId: number) => {
    const key = sourceId;
    setReprocessingId(key);
    try {
      await api.post(`/email-intelligence/${sourceType}/${sourceId}/reprocess`);
      toast.success(`Reprocessed ${sourceType} #${sourceId}`);
      await loadData();
    } catch (error) {
      toast.error('Reprocess failed');
    } finally {
      setReprocessingId(null);
    }
  };

  const handleFeedback = async (recordId: number, correct: boolean) => {
    try {
      await api.post(`/email-intelligence/${recordId}/feedback`, null, {
        params: { correct },
      });
      // Update local state immediately
      setRecords(prev => prev.map(r =>
        r.id === recordId ? { ...r, feedback_correct: correct } : r
      ));
      toast.success(correct ? 'Marked correct' : 'Marked incorrect');
      // Refresh stats in background
      api.get('/email-intelligence/stats').then(res => setStats(res.data));
    } catch (error) {
      toast.error('Failed to save feedback');
    }
  };

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await api.post('/email-intelligence/backfill', null, {
        params: { limit: 50 },
      });
      const data = res.data;
      toast.success(`Backfill complete: ${data.processed} processed, ${data.failed} failed`);
      await loadData();
    } catch (error) {
      toast.error('Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────
  const getClassBadge = (classification: string | null) => {
    const cls = classification || 'unknown';
    const colors = classificationColors[cls] || classificationColors.noise;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono border ${colors.bg} ${colors.text} ${colors.border}`}>
        {cls.replace('_', ' ')}
      </span>
    );
  };

  const getConfidenceBadge = (confidence: number | null) => {
    if (confidence === null || confidence === undefined) return <span className="text-gray-600 text-xs font-mono">--</span>;
    let color = 'text-red-400';
    if (confidence >= 0.9) color = 'text-emerald-400';
    else if (confidence >= 0.75) color = 'text-amber-400';
    return <span className={`text-xs font-mono ${color}`}>{(confidence * 100).toFixed(0)}%</span>;
  };

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return <span className="text-gray-600">--</span>;
    return <span className="text-white font-mono">${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const truncate = (str: string | null, max: number) => {
    if (!str) return '';
    return str.length > max ? str.substring(0, max) + '...' : str;
  };

  // ─── Render ───────────────────────────────────────────────────
  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
          <span className="text-cyan-400">~/</span>intelligence/<span className="text-cyan-400">email-scanner</span>
          <span className="text-gray-600 text-sm font-normal ml-3">
            <span className="text-green-500">$</span> classify --all
          </span>
        </h1>
        <p className="text-gray-500 text-sm font-mono mt-1">
          AI-powered email classification, financial extraction, and ledger matching
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (<>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className={tw.statCard}>
            <div className={tw.statLabel}>Total Processed</div>
            <div className={tw.statValueCyan}>{stats.total}</div>
            <div className="text-gray-600 text-[10px] font-mono mt-1">
              {Object.entries(stats.by_source).map(([k, v]) => `${sourceLabels[k] || k}: ${v}`).join(' · ')}
            </div>
          </div>
          <div className={tw.statCard}>
            <div className={tw.statLabel}>Bills Found</div>
            <div className="text-2xl font-bold font-mono text-red-400">{stats.by_classification?.bill || 0}</div>
            <div className="text-gray-600 text-[10px] font-mono mt-1">
              receipt: {stats.by_classification?.receipt || 0} · payment: {stats.by_classification?.payment_confirmation || 0}
            </div>
          </div>
          <div className={tw.statCard}>
            <div className={tw.statLabel}>Ledger Matched</div>
            <div className="text-2xl font-bold font-mono text-emerald-400">
              {stats.matched}
              {stats.total > 0 && (
                <span className="text-sm text-gray-500 ml-1">
                  ({Math.round((stats.matched / stats.total) * 100)}%)
                </span>
              )}
            </div>
            <div className="text-gray-600 text-[10px] font-mono mt-1">
              avg confidence: {stats.avg_confidence ? `${(stats.avg_confidence * 100).toFixed(0)}%` : '--'}
            </div>
          </div>
          <div className={tw.statCard}>
            <div className={tw.statLabel}>Unmatched Bills</div>
            <div className={`text-2xl font-bold font-mono ${stats.unmatched_bills > 0 ? 'text-amber-400' : 'text-gray-600'}`}>
              {stats.unmatched_bills}
            </div>
            <div className="text-gray-600 text-[10px] font-mono mt-1">
              with property: {stats.with_property}
            </div>
          </div>
        </div>

        {/* Model Accuracy Row */}
        {Object.keys(stats.by_model || {}).length > 0 && (
          <div className="bg-gray-800/60 border border-gray-700/30 rounded-lg px-5 py-3 mb-6 flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <FiCpu className="w-4 h-4 text-gray-500" />
              <span className="text-xs text-gray-500 font-mono uppercase tracking-wider">Model Accuracy</span>
            </div>
            {Object.entries(stats.by_model).map(([model, ms]) => (
              <div key={model} className="flex items-center gap-3">
                <code className="text-[10px] text-cyan-400 bg-gray-900 px-2 py-0.5 rounded border border-gray-700/30 font-mono">
                  {model}
                </code>
                <span className="text-xs text-gray-400 font-mono">
                  {ms.reviewed > 0 ? (
                    <>
                      <span className={ms.accuracy !== null && ms.accuracy >= 0.9 ? 'text-emerald-400' : ms.accuracy !== null && ms.accuracy >= 0.7 ? 'text-amber-400' : 'text-red-400'}>
                        {ms.accuracy !== null ? `${(ms.accuracy * 100).toFixed(0)}%` : '--'}
                      </span>
                      {' '}correct ({ms.correct}/{ms.reviewed} reviewed)
                    </>
                  ) : (
                    <span className="text-gray-600">{ms.total} processed · 0 reviewed</span>
                  )}
                </span>
              </div>
            ))}
            <div className="text-[10px] text-gray-600 font-mono">
              Use <FiThumbsUp className="inline w-3 h-3" />/<FiThumbsDown className="inline w-3 h-3" /> to rate accuracy
            </div>
          </div>
        )}
      </>)}

      {/* Filter Bar + Actions */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <FiFilter className="w-4 h-4 text-gray-500" />
        <select
          value={filterClass}
          onChange={(e) => setFilterClass(e.target.value)}
          className="bg-gray-900 text-gray-300 rounded px-3 py-1.5 border border-gray-700 focus:border-cyan-500 focus:outline-none font-mono text-xs"
        >
          <option value="">All Classifications</option>
          <option value="bill">Bill</option>
          <option value="receipt">Receipt</option>
          <option value="payment_confirmation">Payment Confirmation</option>
          <option value="notice">Notice</option>
          <option value="marketing">Marketing</option>
          <option value="noise">Noise</option>
        </select>
        <select
          value={filterSource}
          onChange={(e) => setFilterSource(e.target.value)}
          className="bg-gray-900 text-gray-300 rounded px-3 py-1.5 border border-gray-700 focus:border-cyan-500 focus:outline-none font-mono text-xs"
        >
          <option value="">All Sources</option>
          <option value="document">Documents</option>
          <option value="hoa_email">HOA Emails</option>
          <option value="retailer_email">Retailer Emails</option>
        </select>
        <select
          value={filterMatch}
          onChange={(e) => setFilterMatch(e.target.value)}
          className="bg-gray-900 text-gray-300 rounded px-3 py-1.5 border border-gray-700 focus:border-cyan-500 focus:outline-none font-mono text-xs"
        >
          <option value="">All Match Status</option>
          <option value="matched">Ledger Matched</option>
          <option value="unmatched">Unmatched</option>
        </select>

        <div className="flex-1" />

        <button
          onClick={handleBackfill}
          disabled={backfilling}
          className={`${tw.btnSecondary} flex items-center gap-2 ${backfilling ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <FiZap className={`w-3.5 h-3.5 ${backfilling ? 'animate-pulse' : ''}`} />
          {backfilling ? 'Processing...' : 'Backfill'}
        </button>
        <button
          onClick={loadData}
          disabled={loading}
          className={`${tw.btnPrimary} flex items-center gap-2`}
        >
          <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Main Table */}
      <div className={tw.tableWrap}>
        <div className="px-5 py-3 border-b border-gray-700/50 flex justify-between items-center">
          <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider">
            Intelligence Records
            <span className="text-gray-600 ml-2">
              ({totalRecords} total{offset > 0 ? `, showing ${offset + 1}–${Math.min(offset + PAGE_SIZE, totalRecords)}` : ''})
            </span>
          </h2>
        </div>

        {loading ? (
          <div className={tw.empty}>
            <div className={tw.loading + ' mx-auto mb-3'}></div>
            <span className="text-gray-500 text-xs font-mono">Loading intelligence data...</span>
          </div>
        ) : records.length === 0 ? (
          <div className={tw.empty}>
            <FiCpu className="w-10 h-10 text-gray-700 mx-auto mb-3" />
            <span className="text-gray-500 text-xs font-mono">No intelligence records found</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700/30">
                  <th className={tw.tableHeader} style={{ width: '24px' }}></th>
                  <th className={tw.tableHeader}>Class</th>
                  <th className={tw.tableHeader}>Source</th>
                  <th className={tw.tableHeader}>Subject</th>
                  <th className={tw.tableHeader}>Vendor</th>
                  <th className={tw.tableHeaderRight}>Amount</th>
                  <th className={tw.tableHeader}>Property</th>
                  <th className={tw.tableHeader}>Ledger</th>
                  <th className={tw.tableHeader}>Processed</th>
                  <th className={tw.tableHeader} style={{ width: '40px' }}></th>
                </tr>
              </thead>
              <tbody className={tw.tableDivider}>
                {records.map((rec) => (
                  <React.Fragment key={rec.id}>
                    {/* Main Row */}
                    <tr
                      className={`${tw.tableRow} cursor-pointer ${expandedId === rec.id ? 'bg-gray-700/20' : ''}`}
                      onClick={() => setExpandedId(expandedId === rec.id ? null : rec.id)}
                    >
                      <td className="pl-3 py-3.5">
                        {expandedId === rec.id
                          ? <FiChevronDown className="w-3.5 h-3.5 text-gray-500" />
                          : <FiChevronRight className="w-3.5 h-3.5 text-gray-600" />
                        }
                      </td>
                      <td className={tw.tableCell}>
                        {getClassBadge(rec.classification)}
                      </td>
                      <td className={tw.tableCell}>
                        <span className="inline-flex items-center gap-1.5">
                          {sourceIcons[rec.source_type]}
                          <span className="text-[10px] text-gray-500 font-mono">{sourceLabels[rec.source_type] || rec.source_type}</span>
                        </span>
                      </td>
                      <td className={tw.tableCell}>
                        <div className="text-sm text-white font-mono leading-tight">{truncate(rec.email_subject, 50)}</div>
                        <div className="text-[10px] text-gray-600 font-mono">{truncate(rec.email_sender, 40)}</div>
                      </td>
                      <td className={tw.tableCell}>
                        <span className="text-sm text-gray-300 font-mono">{rec.vendor || '--'}</span>
                      </td>
                      <td className={tw.tableCellRight}>
                        {formatCurrency(rec.amount)}
                      </td>
                      <td className={tw.tableCell}>
                        {rec.property_name ? (
                          <span className="text-xs text-gray-400 font-mono">{truncate(rec.property_name, 20)}</span>
                        ) : (
                          <span className="text-gray-600 text-xs">--</span>
                        )}
                      </td>
                      <td className={tw.tableCell}>
                        {rec.pocketsmith_transaction_id ? (
                          <span className="inline-flex items-center gap-1">
                            <FiCheckCircle className="w-3 h-3 text-emerald-400" />
                            {getConfidenceBadge(rec.ledger_match_confidence)}
                          </span>
                        ) : rec.classification && ['bill', 'receipt', 'payment_confirmation'].includes(rec.classification) ? (
                          <span className="inline-flex items-center gap-1">
                            <FiAlertTriangle className="w-3 h-3 text-amber-500" />
                            <span className="text-[10px] text-amber-500 font-mono">no match</span>
                          </span>
                        ) : (
                          <span className="text-gray-600 text-xs">--</span>
                        )}
                      </td>
                      <td className={tw.tableCell}>
                        <span className="text-[10px] text-gray-500 font-mono">{formatDateTime(rec.processed_at)}</span>
                      </td>
                      <td className={tw.tableCell}>
                        <div className="flex items-center gap-2">
                          {rec.feedback_correct === true && <FiThumbsUp className="w-3 h-3 text-emerald-400" />}
                          {rec.feedback_correct === false && <FiThumbsDown className="w-3 h-3 text-red-400" />}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReprocess(rec.source_type, rec.source_id);
                            }}
                            disabled={reprocessingId === rec.source_id}
                            className="text-gray-600 hover:text-cyan-400 transition-colors"
                            title="Reprocess"
                          >
                            <FiRefreshCw className={`w-3.5 h-3.5 ${reprocessingId === rec.source_id ? 'animate-spin text-cyan-400' : ''}`} />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Detail Row */}
                    {expandedId === rec.id && (
                      <tr className="bg-gray-900/50">
                        <td colSpan={10} className="px-8 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Left Column — AI Summary + Extraction */}
                            <div className="space-y-3">
                              {rec.summary && (
                                <div>
                                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mb-1">AI Summary</div>
                                  <div className="text-sm text-cyan-400 font-mono bg-gray-800/50 px-3 py-2 rounded border border-gray-700/30">
                                    {rec.summary}
                                  </div>
                                </div>
                              )}
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mb-1">Vendor</div>
                                  <div className="text-sm text-white font-mono">{rec.vendor || '--'}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mb-1">Amount</div>
                                  <div className="text-sm font-mono">{formatCurrency(rec.amount)}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mb-1">Due Date</div>
                                  <div className="text-sm text-white font-mono">{rec.due_date || '--'}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mb-1">Service Date</div>
                                  <div className="text-sm text-white font-mono">{rec.service_date || '--'}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mb-1">Classification</div>
                                  <div className="flex items-center gap-2">
                                    {getClassBadge(rec.classification)}
                                    {getConfidenceBadge(rec.classification_confidence)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mb-1">Source</div>
                                  <div className="text-sm text-gray-400 font-mono">{rec.source_type} #{rec.source_id}</div>
                                </div>
                              </div>
                              {rec.description && (
                                <div>
                                  <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mb-1">Description</div>
                                  <div className="text-xs text-gray-400 font-mono">{rec.description}</div>
                                </div>
                              )}
                            </div>

                            {/* Right Column — Property + Ledger Match */}
                            <div className="space-y-3">
                              <div>
                                <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mb-1">Property</div>
                                {rec.property_name ? (
                                  <div className="text-sm text-white font-mono">
                                    {rec.property_name}
                                    {rec.property_match_method && (
                                      <span className="text-[10px] text-gray-500 ml-2">via {rec.property_match_method}</span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-600 font-mono">No property assigned</div>
                                )}
                              </div>
                              <div>
                                <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mb-1">Ledger Match</div>
                                {rec.pocketsmith_transaction_id ? (
                                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded px-3 py-2">
                                    <div className="flex items-center gap-2 mb-1">
                                      <FiCheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                                      <span className="text-emerald-400 text-xs font-mono font-bold">MATCHED</span>
                                      {getConfidenceBadge(rec.ledger_match_confidence)}
                                    </div>
                                    <div className="text-[10px] text-gray-400 font-mono space-y-0.5">
                                      <div>Transaction #{rec.pocketsmith_transaction_id}</div>
                                      <div>Method: {rec.ledger_match_method || '--'}</div>
                                    </div>
                                  </div>
                                ) : rec.classification && ['bill', 'receipt', 'payment_confirmation'].includes(rec.classification) ? (
                                  <div className="bg-amber-500/10 border border-amber-500/20 rounded px-3 py-2">
                                    <div className="flex items-center gap-2">
                                      <FiAlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                                      <span className="text-amber-400 text-xs font-mono">No ledger match found</span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-sm text-gray-600 font-mono">N/A (non-financial)</div>
                                )}
                              </div>
                              <div>
                                <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mb-1">Email Details</div>
                                <div className="text-xs text-gray-400 font-mono space-y-0.5">
                                  <div><span className="text-gray-600">From:</span> {rec.email_sender || '--'}</div>
                                  <div><span className="text-gray-600">Subject:</span> {rec.email_subject || '--'}</div>
                                  <div><span className="text-gray-600">Processed:</span> {formatDateTime(rec.processed_at)}</div>
                                  {rec.model_used && (
                                    <div><span className="text-gray-600">Model:</span> <code className="text-cyan-400 text-[10px]">{rec.model_used}</code></div>
                                  )}
                                </div>
                              </div>
                              {/* Feedback */}
                              <div className="mt-3 pt-3 border-t border-gray-700/30">
                                <div className="text-[10px] text-gray-500 font-mono uppercase tracking-wider mb-2">Is this correct?</div>
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleFeedback(rec.id, true); }}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-mono transition-all ${
                                      rec.feedback_correct === true
                                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                                        : 'bg-gray-800 text-gray-500 hover:text-emerald-400 border border-gray-700 hover:border-emerald-500/30'
                                    }`}
                                  >
                                    <FiThumbsUp className="w-3.5 h-3.5" /> Correct
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleFeedback(rec.id, false); }}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-mono transition-all ${
                                      rec.feedback_correct === false
                                        ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                                        : 'bg-gray-800 text-gray-500 hover:text-red-400 border border-gray-700 hover:border-red-500/30'
                                    }`}
                                  >
                                    <FiThumbsDown className="w-3.5 h-3.5" /> Incorrect
                                  </button>
                                  {rec.feedback_correct !== null && (
                                    <span className="text-[10px] text-gray-600 font-mono ml-2">
                                      {rec.feedback_correct ? 'Marked correct' : 'Marked incorrect'}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalRecords > PAGE_SIZE && (
          <div className={tw.tableFooter}>
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className={`${tw.btnGhost} ${offset === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
            >
              Previous
            </button>
            <span className="text-gray-500 text-xs font-mono">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, totalRecords)} of {totalRecords}
            </span>
            <button
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= totalRecords}
              className={`${tw.btnGhost} ${offset + PAGE_SIZE >= totalRecords ? 'opacity-30 cursor-not-allowed' : ''}`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
